import { useState, type FormEvent, type ReactNode } from 'react';
import { Wordmark } from './wordmark';

const SESSION_KEY = 'lynrx.preview.unlocked';

function readUnlocked(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function writeUnlocked(): void {
  try {
    sessionStorage.setItem(SESSION_KEY, '1');
  } catch {
    // ignore — sessionStorage may be unavailable (private mode, etc.)
  }
}

export function PasswordGate({
  expected,
  children,
}: {
  expected: string;
  children: ReactNode;
}) {
  const [unlocked, setUnlocked] = useState<boolean>(() => readUnlocked());
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);

  if (unlocked) return <>{children}</>;

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (value === expected) {
      writeUnlocked();
      setUnlocked(true);
      return;
    }
    setError(true);
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg-soft px-4 font-sans">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-white p-8 shadow-sm">
        <div className="flex justify-center">
          <Wordmark />
        </div>
        <h1 className="mt-6 text-center text-lg font-semibold text-ink">
          Preview access
        </h1>
        <p className="mt-1 text-center text-sm text-ink-2">
          This site isn't public yet. Enter the password to continue.
        </p>

        <form onSubmit={onSubmit} className="mt-6 space-y-3">
          <label className="block text-sm">
            <span className="sr-only">Password</span>
            <input
              type="password"
              autoFocus
              autoComplete="off"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                if (error) setError(false);
              }}
              aria-invalid={error || undefined}
              aria-describedby={error ? 'preview-gate-error' : undefined}
              className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-ink shadow-sm outline-none transition-colors focus:border-primary focus:ring-1 focus:ring-primary"
              placeholder="Password"
            />
          </label>

          {error ? (
            <p id="preview-gate-error" className="text-xs text-red-600">
              Incorrect password
            </p>
          ) : null}

          <button
            type="submit"
            className="inline-flex w-full items-center justify-center rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-dark"
          >
            Enter
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-ink-3">
          LYN Rx — invite only preview
        </p>
      </div>
    </main>
  );
}
